import { useEffect, useId, useRef, useState } from 'react';

export interface SearchOption { value: string; label: string; detail?: string }

/**
 * 검색해서 고르는 선택 상자(콤보박스). 항목이 많은 이용자·프로그램 필터용.
 * ↑↓ 로 이동, Enter 로 선택, Esc·바깥 클릭으로 닫힘. options[0] 은 "전체" 항목.
 */
export function SearchSelect({ label, value, options, onChange }: { label: string; value: string; options: SearchOption[]; onChange: (v: string) => void }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const current = options.find((o) => o.value === value) ?? options[0]!;
  const q = query.trim().toLocaleLowerCase('ko-KR');
  const matches = q ? options.filter((o) => `${o.label} ${o.detail ?? ''}`.toLocaleLowerCase('ko-KR').includes(q)) : options;

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  useEffect(() => setActive(0), [query]);
  const pick = (o: SearchOption) => { onChange(o.value); setOpen(false); setQuery(''); };

  return (
    <div className="schedule-search-filter" ref={box} onKeyDown={(e) => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}>
      <span className="bs-lbl" id={`${id}-l`}>{label}</span>
      <button type="button" className="schedule-filter-trigger" aria-haspopup="listbox" aria-expanded={open} aria-labelledby={`${id}-l ${id}-v`} onClick={() => setOpen((o) => !o)}>
        <span id={`${id}-v`}>{current.label}</span><span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="schedule-filter-popup">
          <input ref={input} type="search" value={query} placeholder={`${label} 검색`} onChange={(e) => setQuery(e.target.value)}
            role="combobox" aria-expanded aria-autocomplete="list" aria-controls={`${id}-list`} aria-label={`${label} 검색`}
            aria-activedescendant={matches.length ? `${id}-o${Math.min(active, matches.length - 1)}` : undefined}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setActive((v) => Math.max(0, Math.min(matches.length - 1, v + (e.key === 'ArrowDown' ? 1 : -1)))); }
              if (e.key === 'Enter') { e.preventDefault(); const m = matches[Math.min(active, matches.length - 1)]; if (m) pick(m); }
            }} />
          <div className="schedule-filter-result-label" role="status">{matches.length}개 항목</div>
          <div role="listbox" id={`${id}-list`} aria-label={`${label} 검색 결과`} className="schedule-filter-options">
            {matches.length ? matches.map((o, i) => (
              <button key={o.value || '__all'} id={`${id}-o${i}`} type="button" role="option" aria-selected={o.value === value} className={i === active ? 'keyboard-active' : ''} onClick={() => pick(o)}>
                <strong>{o.label}</strong>{o.detail && <span>{o.detail}</span>}
              </button>
            )) : <p className="hint">검색 결과가 없습니다.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
